import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Loader2, MessageCircle, RotateCcw, Search, Send } from "lucide-react";
import { formatDateTime } from "@/lib/format";

const API_BASE = import.meta.env.VITE_API_URL || "https://api.autoclipper.live";

type LeadSummary = {
  documentId: string;
  name: string;
  email: string;
  phone: string;
  phoneE164: string | null;
  company: string | null;
  state: string | null;
  updatedAt: string;
};

type LeadMessage = {
  id: string;
  direction: string;
  actor: string | null;
  text: string;
  createdAt: string;
};

type OutgoingMessage = LeadMessage & {
  status?: "sending" | "failed";
};

type BriefLine = { label?: string; value?: string };

type LeadBrief = {
  summary?: string;
  project_label?: string;
  missing_fields?: string[];
  capacity_note?: string | null;
  lines?: BriefLine[];
};

type LeadIago = {
  questions_for_gerson?: string[];
};

type LeadDetail = LeadSummary & {
  website: string | null;
  social: string | null;
  demandType: string | null;
  objective: string | null;
  contentType: string[] | string | null;
  platforms: string | null;
  budget: string;
  source: string | null;
  brief: LeadBrief | null;
  iago: LeadIago | null;
  messages: LeadMessage[];
};

type LeadsResponse = {
  data: LeadSummary[];
  total: number;
  page: number;
  pageCount: number;
};

const LIST_QUERY = /* GraphQL */ `
  query AdminBrandLeads($page: Int, $pageSize: Int, $search: String) {
    adminBrandLeads(page: $page, pageSize: $pageSize, search: $search) {
      data {
        documentId
        name
        email
        phone
        phoneE164
        company
        state
        updatedAt
      }
      total
      page
      pageCount
    }
  }
`;

const DETAIL_QUERY = /* GraphQL */ `
  query AdminCpmLead($documentId: ID!) {
    adminCpmLead(documentId: $documentId) {
      documentId
      name
      email
      phone
      phoneE164
      company
      website
      social
      demandType
      objective
      contentType
      platforms
      budget
      source
      state
      brief
      iago
      messages {
        id
        direction
        actor
        text
        createdAt
      }
    }
  }
`;

async function graphql<T>(query: string, variables: Record<string, unknown>, field: string): Promise<T> {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API_BASE}/graphql`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (!res.ok || json.errors?.length) {
    throw new Error(json.errors?.[0]?.message ?? "Failed to load CPM leads");
  }
  return json.data[field] as T;
}

const RESTART_MUTATION = /* GraphQL */ `
  mutation AdminRestartCpmLead($documentId: ID!) {
    adminRestartCpmLead(documentId: $documentId) {
      id
      direction
      actor
      text
      createdAt
    }
  }
`;

const REPLY_MUTATION = /* GraphQL */ `
  mutation AdminReplyCpmLead($documentId: ID!, $text: String!) {
    adminReplyCpmLead(documentId: $documentId, text: $text) {
      id
      direction
      actor
      text
      createdAt
    }
  }
`;

const PAGE_SIZE = 50;

function contentLabel(value: LeadDetail["contentType"]) {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  return value || "";
}

const AdminCpmLeads = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("lead");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [leads, setLeads] = useState<LeadSummary[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [detail, setDetail] = useState<LeadDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [restarting, setRestarting] = useState(false);
  const [outgoing, setOutgoing] = useState<Record<string, OutgoingMessage[]>>({});
  const threadRef = useRef<HTMLDivElement>(null);
  const detailRequest = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    graphql<LeadsResponse>(LIST_QUERY, {
      page: 1,
      pageSize: PAGE_SIZE,
      search: debouncedSearch || undefined,
    }, "adminBrandLeads")
      .then((res) => {
        if (!cancelled) setLeads(res.data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setListError(err instanceof Error ? err.message : t("admin.cpmLeads.loadError"));
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, t]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDetailError(null);
      return undefined;
    }
    const requestId = ++detailRequest.current;
    setDetailLoading(true);
    setDetailError(null);
    graphql<LeadDetail | null>(DETAIL_QUERY, { documentId: selectedId }, "adminCpmLead")
      .then((res) => {
        if (detailRequest.current === requestId) setDetail(res);
      })
      .catch((err: unknown) => {
        if (detailRequest.current !== requestId) return;
        setDetail(null);
        setDetailError(err instanceof Error ? err.message : t("admin.cpmLeads.loadError"));
      })
      .finally(() => {
        if (detailRequest.current === requestId) setDetailLoading(false);
      });
    return () => {
      if (detailRequest.current === requestId) detailRequest.current += 1;
    };
  }, [selectedId, t]);

  const selectLead = (documentId: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (documentId) next.set("lead", documentId);
    else next.delete("lead");
    setDraft("");
    setSearchParams(next);
  };

  const activeDetail = detail?.documentId === selectedId ? detail : null;
  const pendingForLead = selectedId ? outgoing[selectedId] ?? [] : [];
  const visibleMessages: Array<LeadMessage & { status?: OutgoingMessage["status"] }> = [
    ...(activeDetail?.messages ?? []),
    ...pendingForLead,
  ];

  useLayoutEffect(() => {
    const thread = threadRef.current;
    if (!thread) return;
    thread.scrollTop = thread.scrollHeight;
  }, [selectedId, visibleMessages.length, pendingForLead.map((message) => message.status ?? "sent").join("|")]);

  const restartLead = async () => {
    const leadId = selectedId;
    if (!leadId || restarting) return;
    if (!window.confirm(t("admin.cpmLeads.restartConfirm"))) return;
    const requestAtStart = detailRequest.current;
    setRestarting(true);
    setDetailError(null);
    try {
      await graphql(RESTART_MUTATION, { documentId: leadId }, "adminRestartCpmLead");
      const fresh = await graphql<LeadDetail | null>(DETAIL_QUERY, { documentId: leadId }, "adminCpmLead");
      if (detailRequest.current === requestAtStart) setDetail(fresh);
    } catch (err: unknown) {
      setDetailError(err instanceof Error ? err.message : t("admin.cpmLeads.restartError"));
    } finally {
      setRestarting(false);
    }
  };

  const sendReply = async () => {
    const text = draft.trim();
    const leadId = selectedId;
    if (!leadId || !text) return;
    const localId = `pending-${crypto.randomUUID()}`;
    const optimistic: OutgoingMessage = {
      id: localId,
      direction: "outbound",
      actor: "ops",
      text,
      createdAt: new Date().toISOString(),
      status: "sending",
    };
    setOutgoing((current) => ({
      ...current,
      [leadId]: [...(current[leadId] ?? []), optimistic],
    }));
    setDraft("");
    try {
      await graphql(REPLY_MUTATION, { documentId: leadId, text }, "adminReplyCpmLead");
    } catch {
      setOutgoing((current) => ({
        ...current,
        [leadId]: (current[leadId] ?? []).map((message) => (
          message.id === localId ? { ...message, status: "failed" } : message
        )),
      }));
      return;
    }
    setOutgoing((current) => ({
      ...current,
      [leadId]: (current[leadId] ?? []).map((message) => (
        message.id === localId ? { ...message, status: undefined } : message
      )),
    }));
    const requestId = ++detailRequest.current;
    try {
      const fresh = await graphql<LeadDetail | null>(DETAIL_QUERY, { documentId: leadId }, "adminCpmLead");
      if (detailRequest.current === requestId) {
        setDetail((current) => (current?.documentId === leadId ? fresh : current));
      }
      setOutgoing((current) => ({
        ...current,
        [leadId]: (current[leadId] ?? []).filter((message) => message.id !== localId),
      }));
    } catch {
      // The send already landed. Keep the bubble if the thread refresh fails.
    }
  };

  const stateLabel = (state: string | null) => (
    state ? t(`admin.cpmLeads.states.${state}`, { defaultValue: state }) : "—"
  );

  return (
    <Layout>
      <main className="flex-1 overflow-hidden p-4 sm:p-6">
        <div className="mx-auto flex h-[calc(100vh-7rem)] max-w-7xl flex-col gap-4">
          <div className="flex items-center gap-3">
            <MessageCircle className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">{t("admin.cpmLeads.title")}</h1>
              <p className="text-sm text-muted-foreground">{t("admin.cpmLeads.subtitle")}</p>
            </div>
          </div>

          <div className="grid min-h-0 flex-1 gap-4 md:grid-cols-[320px_1fr]">
            <Card className={`min-h-0 overflow-hidden ${selectedId ? "hidden md:flex md:flex-col" : "flex flex-col"}`}>
              <div className="border-b p-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("admin.cpmLeads.searchPlaceholder")}
                    className="pl-9"
                  />
                </div>
              </div>
              <CardContent className="min-h-0 flex-1 overflow-y-auto p-2">
                {listError && (
                  <p className="p-3 text-sm text-destructive">{listError}</p>
                )}
                {listLoading && leads.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">{t("admin.cpmLeads.loading")}</p>
                ) : leads.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">{t("admin.cpmLeads.empty")}</p>
                ) : (
                  <ul className="space-y-1">
                    {leads.map((lead) => {
                      const active = lead.documentId === selectedId;
                      return (
                        <li key={lead.documentId}>
                          <button
                            type="button"
                            onClick={() => selectLead(lead.documentId)}
                            className={`w-full rounded-md px-3 py-2 text-left ${
                              active ? "bg-primary/10" : "hover:bg-muted"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate font-medium">{lead.name}</span>
                              <Badge variant="secondary" className="shrink-0 text-[10px]">
                                {stateLabel(lead.state)}
                              </Badge>
                            </div>
                            <p className="truncate text-xs text-muted-foreground">
                              {lead.company || lead.phone}
                            </p>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card className={`min-h-0 overflow-hidden ${selectedId ? "flex flex-col" : "hidden md:flex md:flex-col"}`}>
              {!selectedId ? (
                <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
                  {t("admin.cpmLeads.pickLead")}
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-3 border-b p-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="md:hidden"
                      onClick={() => selectLead(null)}
                      aria-label={t("admin.cpmLeads.back")}
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className="min-w-0">
                      <h2 className="truncate text-lg font-semibold">
                        {activeDetail?.name || leads.find((lead) => lead.documentId === selectedId)?.name}
                      </h2>
                      <p className="truncate text-sm text-muted-foreground">
                        {[activeDetail?.company, activeDetail?.phoneE164 || activeDetail?.phone].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <div className="ml-auto flex shrink-0 items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={
                          !activeDetail
                          || restarting
                          || activeDetail.state === "opted_out"
                          || activeDetail.state === "lost"
                        }
                        onClick={restartLead}
                      >
                        {restarting ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <RotateCcw className="mr-2 h-4 w-4" />
                        )}
                        {restarting ? t("admin.cpmLeads.restarting") : t("admin.cpmLeads.restart")}
                      </Button>
                      {activeDetail?.state && (
                        <Badge variant="secondary">
                          {stateLabel(activeDetail.state)}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {activeDetail && (
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 border-b px-4 py-3 text-sm lg:grid-cols-3">
                      {[
                        [t("admin.cpmLeads.form.profile"), activeDetail.demandType],
                        [t("admin.cpmLeads.form.objective"), activeDetail.objective],
                        [t("admin.cpmLeads.form.content"), contentLabel(activeDetail.contentType)],
                        [t("admin.cpmLeads.form.platforms"), activeDetail.platforms],
                        [t("admin.cpmLeads.form.budget"), activeDetail.budget ? t(`admin.cpmLeads.budgets.${activeDetail.budget}`, { defaultValue: activeDetail.budget }) : ""],
                        [t("admin.cpmLeads.form.company"), activeDetail.company],
                        [t("admin.cpmLeads.form.email"), activeDetail.email],
                        [t("admin.cpmLeads.form.phone"), activeDetail.phoneE164 || activeDetail.phone],
                        [t("admin.cpmLeads.form.website"), activeDetail.website],
                        [t("admin.cpmLeads.form.social"), activeDetail.social],
                      ].filter(([, value]) => String(value || "").trim()).map(([label, value]) => (
                        <div key={label} className="min-w-0">
                          <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</dt>
                          <dd className="truncate">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {activeDetail?.brief && (
                    <div className="max-h-56 space-y-2 overflow-y-auto border-b px-4 py-3 text-sm">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {t("admin.cpmLeads.briefTitle")}
                        {activeDetail.brief.project_label ? ` · ${activeDetail.brief.project_label}` : ""}
                      </p>
                      {activeDetail.brief.summary && (
                        <p className="whitespace-pre-wrap">{activeDetail.brief.summary}</p>
                      )}
                      {(activeDetail.brief.lines || []).filter((line) => line.value).length > 0 && (
                        <dl className="grid gap-2 sm:grid-cols-2">
                          {(activeDetail.brief.lines || []).filter((line) => line.value).map((line) => (
                            <div key={`${line.label}-${line.value}`} className="min-w-0">
                              <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{line.label}</dt>
                              <dd className="whitespace-pre-wrap">{line.value}</dd>
                            </div>
                          ))}
                        </dl>
                      )}
                      {(activeDetail.brief.missing_fields || []).length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {t("admin.cpmLeads.briefMissing")}: {activeDetail.brief.missing_fields?.join(", ")}
                        </p>
                      )}
                      {activeDetail.brief.capacity_note && (
                        <p className="text-xs">{activeDetail.brief.capacity_note}</p>
                      )}
                      {(activeDetail.iago?.questions_for_gerson || []).length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            {t("admin.cpmLeads.diagnosisTitle")}
                          </p>
                          <ul className="mt-1 list-disc pl-4">
                            {activeDetail.iago?.questions_for_gerson?.map((question) => (
                              <li key={question}>{question}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  <CardContent ref={threadRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
                    {detailError && (
                      <p className="text-sm text-destructive">{detailError}</p>
                    )}
                    {detailLoading && visibleMessages.length === 0 && (
                      <p className="text-sm text-muted-foreground">{t("admin.cpmLeads.loading")}</p>
                    )}
                    {!detailLoading && visibleMessages.length === 0 && (
                      <p className="text-sm text-muted-foreground">{t("admin.cpmLeads.noMessages")}</p>
                    )}
                    {visibleMessages.map((message) => {
                      const fromLara = message.direction === "outbound";
                      const sending = message.status === "sending";
                      const failed = message.status === "failed";
                      return (
                        <div key={message.id} className={`flex ${fromLara ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                            fromLara ? "bg-primary/10" : "bg-muted"
                          } ${sending ? "opacity-70" : ""} ${failed ? "ring-1 ring-destructive/50" : ""}`}
                          >
                            <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                              <span>
                                {fromLara
                                  ? (message.actor === "ops" ? t("admin.cpmLeads.team") : t("admin.cpmLeads.lara"))
                                  : t("admin.cpmLeads.lead")}
                              </span>
                              {sending ? (
                                <span className="inline-flex items-center gap-1 normal-case">
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  {t("admin.cpmLeads.sending")}
                                </span>
                              ) : failed ? (
                                <span className="normal-case text-destructive">{t("admin.cpmLeads.sendFailed")}</span>
                              ) : (
                                <span>{formatDateTime(message.createdAt)}</span>
                              )}
                            </div>
                            <p className="whitespace-pre-wrap">{message.text}</p>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                  <form
                    className="flex items-end gap-2 border-t p-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      sendReply();
                    }}
                  >
                    <Textarea
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      placeholder={t("admin.cpmLeads.replyPlaceholder")}
                      rows={2}
                      className="min-h-[64px] resize-none"
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                          event.preventDefault();
                          sendReply();
                        }
                      }}
                    />
                    <Button type="submit" disabled={!draft.trim()} className="shrink-0">
                      <Send className="mr-2 h-4 w-4" />
                      {t("admin.cpmLeads.send")}
                    </Button>
                  </form>
                </>
              )}
            </Card>
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default AdminCpmLeads;
